import { cleanupTiptapHtml } from 'components/RichTextEditor/hooks/utilities';
import { flatten } from 'flat';
import { mergeDeep } from 'remeda';
import { FullEditorialAsset } from 'utility/types/dataProvider';

/**
 * Common Preview Payload for Editorial
 */
export const commonPreviewPayload = {
  preview: {
    size1: '550w',
    size2: '750w',
    thumbnail: '550w',
    display: '750w',
    preview: '500w',
  },
};

/**
 * Note: before using flattenObject, consider if using
 * qs.stringify + default qsOptions (in httpClient.ts) can
 * be used as an alternative to flattening the object manually.
 * eg: { previews: { size1:'550w' } } -> 'previews[size1]': '550w'
 *
 * Flatten objects for square bracket syntax - https://github.com/hughsk/flat
 * eg: "preview[display]: "750w", handle[deep][nested][values]: "yes"
 **/

export function flattenObject<T extends Record<string, any>>(
  input: T,
  bracketed = true,
): Record<string, string> {
  const flattened = flatten<T, Record<string, any>>(input, {
    delimiter: '.',
    safe: true,
  });

  // Optionally return early without bracketed syntax
  if (!bracketed) {
    return flattened;
  }

  // Post-process each flattened key to convert dots into bracket notation
  // Example: "preview.size1" → "preview[size1]"
  const formatted = Object.fromEntries(
    Object.entries(flattened).map(([flatKey, value]) => {
      const bracketedKey = flatKey
        .split('.')
        .reduce((acc, segment, i) => (i === 0 ? segment : `${acc}[${segment}]`), '');
      return [bracketedKey, String(value)];
    }),
  );

  return formatted;
}

/**
 * Process related content/children of Partial<FullEditorialAsset>
 **/
export function cleanupRelatedContent(params: any) {
  if (!params?.content) {
    return params;
  }

  const htmlString = params.content;
  // Create a new DOMParser
  const parser = new DOMParser();
  // Parse the HTML string to create an HTMLElement
  const doc = parser.parseFromString(htmlString, 'text/html');
  const element = doc.documentElement;
  const bloxInlineElements = element.querySelectorAll('blox-inline');
  const extractedData = [];

  for (const bloxInlineElement of bloxInlineElements) {
    if (bloxInlineElement.getAttribute('placeholder') === 'true') {
      continue; // Skip processing if placeholder is true
    }

    /** Prepare and push assets to extracted data */
    const uuid = bloxInlineElement.getAttribute('uuid');
    const app = bloxInlineElement.getAttribute('app');
    const type = bloxInlineElement.getAttribute('type') ?? '';

    if (uuid && app) {
      extractedData.push({
        uuid: uuid,
        app: app,
        type: type,
      });
    }
  }

  /**
   * Check to see that extractedData.app is an appwith assets before jamming in.
   * Merge children and extractedData, removing duplicates by UUID
   * Also skip inline assets that are already in siblings or parents arrays
   * Ensure duplicates are not added as the API will reject the payload - TNCMS-510151
   **/
  let children: any[] = [];
  if (params.children) {
    // Start with existing children
    children = [...params.children];

    // Collect UUIDs from all relationship arrays to prevent duplicates
    const existingUuids = new Set(
      children.map((child: any) => child.uuid).filter(Boolean),
    );

    // Also collect UUIDs from siblings and parents to avoid duplicates there
    const siblingUuids = new Set(
      (params.siblings || []).map((sibling: any) => sibling.uuid).filter(Boolean),
    );
    const parentUuids = new Set(
      (params.parents || []).map((parent: any) => parent.uuid).filter(Boolean),
    );

    // Add extractedData items only if they don't exist in any relationship array
    extractedData.forEach((item: any) => {
      if (
        item.uuid &&
        !existingUuids.has(item.uuid) &&
        !siblingUuids.has(item.uuid) &&
        !parentUuids.has(item.uuid)
      ) {
        children.push(item);
        existingUuids.add(item.uuid);
      }
    });
  } else {
    children = extractedData;
  }

  const transformedData = {
    ...params,
    children: children,
  };

  return transformedData;
}

/**
 * API expects byline line breaks as <br> rather than block wrappers like <p>. TNCMS-556561
 */
function formatBylineForSave(dirty?: string | null) {
  if (!dirty) {
    return dirty;
  }

  const cleaned = cleanupTiptapHtml({ dirty }) ?? '';

  const withBreaks = cleaned
    .replace(/<\/(p|div)>\s*<(p|div)[^>]*>/gi, '<br>') // Replace closing and opening block tags with <br>
    .replace(/<(p|div)[^>]*>/gi, '') // Remove opening block tags
    .replace(/<\/(p|div)>/gi, '') // Remove closing block tags
    .trim();

  // Collapse excessive consecutive breaks and trim trailing breaks.
  return withBreaks
    .replace(/(?:<br\s*\/?>(\s|&nbsp;)*){3,}/gi, '<br><br>')
    .replace(/(?:\s|&nbsp;)*(<br\s*\/?>\s*)+$/gi, '')
    .trim();
}

/**
 * Handle Cleanup of Various Attributes from Editorial Asset Payload
 **/
export function cleanupAssetFields(params: Partial<FullEditorialAsset>) {
  if (!params) {
    return params;
  }

  // unsanitized tiptap html content
  const dirtyContent = params?.content;
  const cleanContent = cleanupTiptapHtml({ dirty: dirtyContent });

  const dirtyTagline = params?.properties?.tagline;
  const cleanTagline = cleanupTiptapHtml({ dirty: dirtyTagline });

  const dirtyByline = params?.byline;
  const cleanByline = formatBylineForSave(dirtyByline);

  const dirtySummary = params?.summary;
  const cleanSummary = cleanupTiptapHtml({ dirty: dirtySummary });

  const cleanedFields = {
    content: cleanContent ?? '',
    byline: cleanByline ?? '',
    summary: cleanSummary ?? '',
    properties: {
      tagline: cleanTagline ?? '',
    },
  };

  // https://remedajs.com/docs/#mergeDeep
  // merge operation prioritizes the second objects properties
  // if both first and second obj are a objects the result
  // is taken as the result of recursively deepMerging them
  const merged = mergeDeep(params, cleanedFields);

  return merged;
}
